import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
import React, { useId, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../hooks/useApi';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import toast from 'react-hot-toast';

import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Icon } from '../components/ui/Icon';
import { Modal } from '../components/ui/Modal';
import { FormField, SelectField } from '../components/forms';
import { SuppliersTable } from '../components/suppliers';
import { supplierSchemas } from '../components/forms/validation';
import { apiService } from '../services/api';
import { Supplier } from '../types/api';
import { SupplierCreateForm, SupplierUpdateForm } from '../types/forms';

// ============================================================================
// Validation Schemas
// ============================================================================

// Use centralized Yup schemas from validation.ts
const supplierCreateSchema = supplierSchemas.supplierCreate;

type SupplierFormData = yup.InferType<typeof supplierCreateSchema>;

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;

  /* Mobile devices - stack vertically */
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 1.5rem;
  }
`;

const Title = styled(SharedPageTitle)``;

const CreateButton = styled(Button)`
  background: var(--action-primary);
  color: white;
  border: none;

  &:hover {
    background: var(--action-primary-hover);
  }

  /* Mobile devices - full width */
  @media (max-width: 767px) {
    width: 100%;
  }
`;

// Table container for consistent spacing
const SuppliersTableContainer = styled.div`
  margin-top: 2rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-muted);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  /* Mobile devices - reduced gap */
  @media (max-width: 767px) {
    gap: 1rem;
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid var(--border);

  /* Mobile devices - full width buttons */
  @media (max-width: 767px) {
    flex-direction: column-reverse;
    gap: 0.75rem;

    button {
      width: 100%;
    }
  }
`;

const CertificationField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CertificationTooltip = styled.p`
  margin: 0;
  padding: 0.75rem;
  font-size: 0.85rem;
  line-height: 1.4;
  color: var(--text-muted);
  border-radius: var(--radius-md);
  background-color: var(--surface-strong);
  border-left: 3px solid var(--brand-300);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

// ============================================================================
// Component
// ============================================================================

export const SuppliersPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [deletingSupplier, setDeletingSupplier] = useState<Supplier | null>(
    null,
  );

  const queryClient = useQueryClient();

  // Query for fetching suppliers
  const {
    data: suppliers = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.suppliers.all,
    queryFn: () => apiService.getSuppliers(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Form for creating/editing suppliers
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SupplierFormData>({
    resolver: yupResolver(supplierCreateSchema) as any,
    defaultValues: {
      name: '',
      description: undefined,
      has_certification: false,
      status: 'active',
    },
  });

  const certificationTooltipId = useId();

  // Create supplier mutation
  const createMutation = useMutation({
    mutationFn: (data: SupplierCreateForm) => apiService.createSupplier(data),
    onSuccess: (_newSupplier) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      // Force refetch dashboard data since supplier certification affects dashboard metrics
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      toast.success('Proveedor creado exitosamente');
      setIsCreateModalOpen(false);
      reset();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail || 'Error al crear el proveedor',
      );
    },
  });

  // Update supplier mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: SupplierUpdateForm }) =>
      apiService.updateSupplier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      // Force refetch dashboard data since supplier certification affects dashboard metrics
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      toast.success('Proveedor actualizado exitosamente');
      setEditingSupplier(null);
      reset();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail ||
          error.message ||
          'Error al actualizar el proveedor',
      );
    },
  });

  // Delete supplier mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
      // Force refetch dashboard data since supplier certification affects dashboard metrics
      queryClient.refetchQueries({ queryKey: queryKeys.dashboard.summary });
      toast.success('Proveedor eliminado exitosamente');
      setDeletingSupplier(null);
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail || 'Error al eliminar el proveedor',
      );
    },
  });

  // Handler functions
  const handleCreateSupplier = () => {
    reset({
      name: '',
      description: '',
      has_certification: false,
      status: 'active',
    });
    setIsCreateModalOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    reset({
      name: supplier.name,
      description: supplier.description || '',
      has_certification: supplier.has_certification,
      status: supplier.status,
    });
    setEditingSupplier(supplier);
  };

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    setEditingSupplier(null);
    reset();
  };

  const onSubmit = async (data: SupplierFormData) => {
    if (editingSupplier) {
      // For updates, only send changed fields by comparing with original supplier
      const updateData: SupplierUpdateForm = {};

      // Compare each field with the original supplier data
      if (data.name !== editingSupplier.name) {
        updateData.name = data.name;
      }
      // Correctly compare description, handling null/undefined/empty string
      if ((data.description || '') !== (editingSupplier.description || '')) {
        updateData.description = data.description || '';
      }
      if (data.has_certification !== editingSupplier.has_certification) {
        updateData.has_certification = data.has_certification;
      }
      if (data.status !== editingSupplier.status) {
        updateData.status = data.status;
      }

      // Only proceed with update if there are actual changes
      if (Object.keys(updateData).length > 0) {
        updateMutation.mutate({ id: editingSupplier.id, data: updateData });
      } else {
        // No changes made, just close the modal
        toast('No se realizaron cambios en el proveedor');
        setEditingSupplier(null);
        reset();
      }
    } else {
      const createData: SupplierCreateForm = {
        name: data.name,
        has_certification: data.has_certification,
        status: data.status,
        ...(data.description && { description: data.description }),
      };
      createMutation.mutate(createData);
    }
  };

  const handleDeleteSupplier = (supplier: Supplier) => {
    setDeletingSupplier(supplier);
  };

  const confirmDelete = () => {
    if (deletingSupplier) {
      deleteMutation.mutate(deletingSupplier.id);
    }
  };

  // Status options for select field
  const statusOptions = [
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
  ];

  if (error) {
    return (
      <PageContainer>
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h3 style={{ color: 'var(--danger-600)', marginBottom: '1rem' }}>
              Error al cargar proveedores
            </h3>
            <p style={{ color: 'var(--text-muted)' }}>
              No se pudieron cargar los proveedores. Por favor, inténtalo de
              nuevo.
            </p>
            <Button
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: ['suppliers'] })
              }
              style={{ marginTop: '1rem' }}
            >
              Reintentar
            </Button>
          </div>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <div>
          <Title>Proveedores</Title>
          <p style={{ color: 'var(--text-muted)', margin: '0.5rem 0 0 0' }}>
            Gestiona los proveedores de tu organización
          </p>
          <div
            id="suppliers-table-description"
            style={{ display: 'none' }}
            aria-hidden="true"
          >
            Tabla de proveedores con información sobre nombre, estado,
            certificación, descripción y fecha de creación.
          </div>
        </div>
        <CreateButton onClick={handleCreateSupplier}>
          <Icon name="add" size="sm" aria-hidden /> Agregar Proveedor
        </CreateButton>
      </Header>

      {isLoading ? (
        <LoadingContainer>
          <LoadingSpinner message="Cargando proveedores..." />
        </LoadingContainer>
      ) : suppliers.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyIcon>
              <Icon name="building" size="xl" aria-hidden />
            </EmptyIcon>
            <h3 style={{ marginBottom: '1rem' }}>
              No hay proveedores registrados
            </h3>
            <p style={{ marginBottom: '2rem' }}>
              Comienza agregando tu primer proveedor para gestionar tu cadena de
              suministro.
            </p>
            <Button onClick={handleCreateSupplier}>
              <Icon name="add" size="sm" aria-hidden /> Agregar Primer Proveedor
            </Button>
          </EmptyState>
        </Card>
      ) : (
        <SuppliersTableContainer>
          <SuppliersTable
            suppliers={suppliers}
            onEdit={handleEditSupplier}
            onDelete={handleDeleteSupplier}
            loading={isLoading}
            onSort={(_key, _direction) => {
              // For now, we'll let the table handle client-side sorting
              // In the future, this could trigger server-side sorting
            }}
          />
        </SuppliersTableContainer>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isCreateModalOpen || !!editingSupplier}
        onClose={handleCloseModal}
        title={editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGrid>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <FormField
                  label="Nombre del proveedor"
                  required
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <div>
                  <label
                    htmlFor="description-field"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                    }}
                  >
                    Descripción
                  </label>
                  <textarea
                    {...field}
                    id="description-field"
                    rows={3}
                    placeholder="Descripción del proveedor (opcional)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      backgroundColor: 'var(--surface-strong)',
                      color: 'var(--text-primary)',
                    }}
                    data-testid="description-field"
                  />
                  {errors.description?.message && (
                    <p
                      style={{
                        color: 'var(--danger-600)',
                        fontSize: '0.875rem',
                        marginTop: '0.25rem',
                      }}
                    >
                      {errors.description.message}
                    </p>
                  )}
                </div>
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <SelectField
                  label="Estado"
                  options={statusOptions}
                  error={errors.status?.message}
                  required
                  {...field}
                />
              )}
            />

            <Controller
              name="has_certification"
              control={control}
              render={({ field: { value, onChange, ...field } }) => (
                <CertificationField>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <input
                      type="checkbox"
                      aria-describedby={certificationTooltipId}
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                      {...field}
                    />
                    <span>¿Tiene certificaciones?</span>
                  </label>
                  <CertificationTooltip
                    id={certificationTooltipId}
                    role="note"
                    aria-live="polite"
                  >
                    Un proveedor es considerado "Sostenible/Certificado" en la
                    plataforma FoodPrint cuando el ingrediente, producto o
                    servicio específico que suministra cuenta con una
                    certificación de tercera parte o un sello sectorial
                    reconocido y verificable que aborda, al menos, uno de los
                    tres pilares de la sostenibilidad. Recomendamos siempre
                    revisar el documento de respaldo de la certificación.
                  </CertificationTooltip>
                </CertificationField>
              )}
            />
          </FormGrid>

          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCloseModal}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>⏳ {editingSupplier ? 'Actualizando...' : 'Creando...'}</>
              ) : (
                <>{editingSupplier ? 'Actualizar' : 'Crear'} Proveedor</>
              )}
            </Button>
          </FormActions>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingSupplier}
        onClose={() => setDeletingSupplier(null)}
        title="Confirmar eliminación"
      >
        <div>
          <p style={{ marginBottom: '1.5rem' }}>
            ¿Estás seguro de que quieres eliminar el proveedor{' '}
            <strong>{deletingSupplier?.name}</strong>?
          </p>
          <p
            style={{
              color: 'var(--text-muted)',
              fontSize: '0.9rem',
              marginBottom: '2rem',
            }}
          >
            Esta acción no se puede deshacer.
          </p>
          <FormActions>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setDeletingSupplier(null)}
              disabled={deleteMutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
              style={{
                background: 'var(--danger-600)',
                color: 'white',
                border: 'none',
              }}
            >
              {deleteMutation.isPending ? (
                <>
                  <Icon name="loading" size="sm" aria-hidden /> Eliminando...
                </>
              ) : (
                <>
                  <Icon name="delete" size="sm" aria-hidden /> Eliminar
                </>
              )}
            </Button>
          </FormActions>
        </div>
      </Modal>
    </PageContainer>
  );
};

export default SuppliersPage;
